import { Card, CardActions, CardContent, Grid, Typography } from '@mui/material';
import { useFormik } from 'formik';
import { useCallback } from 'react';
import * as yup from 'yup';
import { PmiButton } from 'shell/components/shared/button';
import PmiTextField from 'shell/components/shared/text-field';
import TypeEventList from '../type-event-list';
import TypeDestinationList from '../type-destination-list';
import SubscribertList from '../subscriber-list';
import MicroConsumerList from '../microservizio-consumer-list';
import HttpList from '../http-list';

export interface EventRegistrationForm {
  eventCode?: string;
  destinationCode?: string;
  subscriberCode?: string;
  progressive?: string;
  microConsumer?: string;
  httpMethod?: string;
  eventDescription?: string;
  subscriberDescription?: string;
  destinationDescription?: string;
  pathConsumer?: string;
}

const validationSchema = yup.object().shape({
  eventCode: yup.string().required('Il campo codice Tipo Evento è obbligatorio'),
  destinationCode: yup.string().required('Il campo codice Descrizione Evento è obbligatorio'),
  subscriberCode: yup.string().required('Il campo codice Sottoscrittore è obbligatorio'),
  progressive: yup.string().required('Il campo Progressivo è obbligatorio'),
  microConsumer: yup.string().required('Il campo codice Sottoscrittore è obbligatorio'),
  httpMethod: yup.string().required('Il campo Metodo Http è obbligatorio'),
  pathConsumer: yup.string().required('Il campo Path Specifico Consumer è obbligatorio'),
});

interface FormTypeEventProps {
  onOk: (form: EventRegistrationForm) => void;
  onCancel: () => void;
  title: string;
  disabled?: boolean;
  initialValues?: EventRegistrationForm;
  isEdit?: boolean;
  isDetail?: boolean;
}

const FormEventRegistration = ({
  onOk,
  onCancel,
  title,
  disabled = false,
  initialValues,
  isDetail = false,
  isEdit = false,
}: FormTypeEventProps): React.ReactElement => {
  const onSubmit = useCallback(
    (formValues) => {
      onOk(formValues);
    },
    [onOk]
  );

  const formTypeEvent = useFormik<EventRegistrationForm>({
    initialValues: initialValues || {
      eventCode: '',
      destinationCode: '',
      subscriberCode: '',
      progressive: '',
      microConsumer: '',
      httpMethod: '',
      pathConsumer: '',
      eventDescription: '',
      subscriberDescription: '',
      destinationDescription: '',
    },
    validationSchema: validationSchema,
    onSubmit,
    enableReinitialize: true,
  });

  return (
    <Card raised className="pmi-my-3">
      <form onSubmit={formTypeEvent.handleSubmit}>
        <CardContent style={{ padding: '24px' }}>
          <Typography gutterBottom variant="h5" component="h2" color="secondary">
            {title}
          </Typography>
          <div style={{ padding: '24px 0 0 0' }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12}>
                <TypeEventList
                  value={formTypeEvent.values.eventCode || ''}
                  onChange={(e) => formTypeEvent.setFieldValue('eventCode', e.target.value)}
                  required
                  disabled={isEdit || disabled}
                  onBlur={() => formTypeEvent.setFieldTouched('eventCode', true)}
                  error={formTypeEvent.touched.eventCode && Boolean(formTypeEvent.errors.eventCode)}
                />
              </Grid>
              {isDetail || isEdit ? (
                <Grid item xs={12} md={10}>
                  <PmiTextField
                    aria-label="Descrizione del Tipo Evento"
                    label="Descrizione Tipo Evento"
                    id="eventDescription"
                    name="eventDescription"
                    type="text"
                    variant="outlined"
                    fullWidth
                    value={formTypeEvent.values.eventDescription || ''}
                    disabled={disabled || isEdit}
                    onChange={formTypeEvent.handleChange}
                    error={
                      formTypeEvent.touched.eventDescription &&
                      Boolean(formTypeEvent.errors.eventDescription)
                    }
                    helperText={formTypeEvent.errors.eventDescription}
                  />
                </Grid>
              ) : undefined}
              <Grid item xs={12}>
                <TypeDestinationList
                  value={formTypeEvent.values.destinationCode || ''}
                  onChange={(e) => formTypeEvent.setFieldValue('destinationCode', e.target.value)}
                  disabled={isEdit || disabled}
                  required
                  onBlur={() => formTypeEvent.setFieldTouched('destinationCode', true)}
                  error={
                    formTypeEvent.touched.destinationCode &&
                    Boolean(formTypeEvent.errors.destinationCode)
                  }
                />
              </Grid>
              {isDetail || isEdit ? (
                <Grid item xs={12} md={10}>
                  <PmiTextField
                    aria-label="Descrizione del Tipo Destinazione"
                    label="Descrizione Tipo Destinazione"
                    id="destinationDescription"
                    name="destinationDescription"
                    type="text"
                    variant="outlined"
                    fullWidth
                    value={formTypeEvent.values.destinationDescription || ''}
                    disabled={disabled || isEdit}
                    onChange={formTypeEvent.handleChange}
                    error={
                      formTypeEvent.touched.destinationDescription &&
                      Boolean(formTypeEvent.errors.destinationDescription)
                    }
                    helperText={formTypeEvent.errors.destinationDescription}
                  />
                </Grid>
              ) : undefined}
              <Grid item xs={12}>
                <SubscribertList
                  value={formTypeEvent.values.subscriberCode || ''}
                  onChange={(e) => formTypeEvent.setFieldValue('subscriberCode', e.target.value)}
                  required
                  disabled={isEdit || disabled}
                  onBlur={() => formTypeEvent.setFieldTouched('subscriberCode', true)}
                  error={
                    formTypeEvent.touched.subscriberCode &&
                    Boolean(formTypeEvent.errors.subscriberCode)
                  }
                />
              </Grid>
              {isDetail || isEdit ? (
                <Grid item xs={12} md={10}>
                  <PmiTextField
                    aria-label="Descrizione del Sottoscrittore"
                    label="Descrizione Sottoscrittore"
                    id="subscriberDescription"
                    name="subscriberDescription"
                    type="text"
                    variant="outlined"
                    fullWidth
                    value={formTypeEvent.values.subscriberDescription || ''}
                    disabled={disabled || isEdit}
                    onChange={formTypeEvent.handleChange}
                    error={
                      formTypeEvent.touched.subscriberDescription &&
                      Boolean(formTypeEvent.errors.subscriberDescription)
                    }
                    helperText={formTypeEvent.errors.subscriberDescription}
                  />
                </Grid>
              ) : undefined}
              <Grid item xs={12} md={2}>
                <PmiTextField
                  aria-label="Progressivo"
                  label="Progressivo"
                  id="progressive"
                  name="progressive"
                  required
                  type="text"
                  variant="outlined"
                  fullWidth
                  value={formTypeEvent.values.progressive || ''}
                  disabled={disabled || isEdit}
                  onChange={formTypeEvent.handleChange}
                  error={
                    formTypeEvent.touched.progressive && Boolean(formTypeEvent.errors.progressive)
                  }
                  helperText={formTypeEvent.errors.progressive}
                />
              </Grid>
              <Grid item xs={12} md={9}>
                <MicroConsumerList
                  value={formTypeEvent.values.microConsumer || ''}
                  onChange={formTypeEvent.handleChange}
                  disabled={disabled}
                  onBlur={() => formTypeEvent.setFieldTouched('microConsumer', true)}
                  error={
                    formTypeEvent.touched.microConsumer &&
                    Boolean(formTypeEvent.errors.microConsumer)
                  }
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <HttpList
                  value={formTypeEvent.values.httpMethod || ''}
                  onChange={(e) => formTypeEvent.setFieldValue('httpMethod', e.target.value)}
                  disabled={disabled}
                  onBlur={() => formTypeEvent.setFieldTouched('httpMethod', true)}
                  error={
                    formTypeEvent.touched.httpMethod && Boolean(formTypeEvent.errors.httpMethod)
                  }
                />
              </Grid>
              <Grid item xs={12} md={9}>
                <PmiTextField
                  aria-label="Path specifico consumer"
                  label="Path specifico consumer*"
                  id="pathConsumer"
                  name="pathConsumer"
                  type="text"
                  variant="outlined"
                  fullWidth
                  value={formTypeEvent.values.pathConsumer || ''}
                  disabled={disabled}
                  onChange={formTypeEvent.handleChange}
                  error={
                    formTypeEvent.touched.pathConsumer && Boolean(formTypeEvent.errors.pathConsumer)
                  }
                  helperText={formTypeEvent.errors.pathConsumer}
                />
              </Grid>
            </Grid>
          </div>
        </CardContent>
        <CardActions style={{ padding: '24px 24px 16px 24px' }}>
          <PmiButton
            variant="contained"
            color="primary"
            type="submit"
            disabled={disabled || !formTypeEvent.isValid}
          >
            Conferma
          </PmiButton>
          <PmiButton
            variant="outlined"
            color="cdmBrand"
            onClick={() => {
              onCancel();
            }}
          >
            Indietro
          </PmiButton>
        </CardActions>
      </form>
    </Card>
  );
};

FormEventRegistration.displayName = 'Form Event Registration component';

export default FormEventRegistration;
